import axios, { InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { API_URL } from '../config';

// Tạo một global loading manager
let loadingManager: {
  showLoader: () => void;
  hideLoader: () => void;
} | null = null;

// Hàm để set loading manager từ LoadingContext
export const setLoadingManager = (manager: { showLoader: () => void; hideLoader: () => void }) => {
  loadingManager = manager;
};

// Cấu hình mặc định cho axios
const axiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  },
  timeout: 100000,
});

// Thêm interceptor để xử lý token và loading
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => { 
    // Show loader
    if (loadingManager) {
      loadingManager.showLoader();
    }
    
    // Đảm bảo headers luôn tồn tại
    if (!config.headers) {
      config.headers = {} as any;
    }
    
    // Nếu body là FormData, xóa Content-Type để browser tự set với boundary
    if (config.data instanceof FormData) {
      const headersAny: any = config.headers as any;
      if (headersAny?.set) {
        headersAny.set('Content-Type', undefined);
        headersAny.set('content-type', undefined);
      }
      delete headersAny?.['Content-Type'];
      delete headersAny?.['content-type'];
    }

    // Lấy token từ localStorage
    try {
      const token = localStorage.getItem('access_token');
      if (token) {
        // Kiểm tra xem token đã có prefix Bearer chưa
        const tokenWithBearer = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
        config.headers.Authorization = tokenWithBearer;
      }
    } catch (error) {

    }
    
    return config;
  },
  (error: AxiosError) => {
    // Hide loader on error
    if (loadingManager) {
      loadingManager.hideLoader();
    }
    return Promise.reject(error);
  }
);

// Xử lý response
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    // Hide loader on success
    if (loadingManager) {
      loadingManager.hideLoader();
    }
    return response;
  },
  async (error: AxiosError) => {
    // Hide loader on error
    if (loadingManager) {
      loadingManager.hideLoader();
    }
    
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname;
      const requestUrl = error.config?.url || '';
      
      // Không redirect nếu:
      // 1. Đang ở trang login hoặc callback
      // 2. Đang gọi API check auth (/auth/user) - để AuthContext xử lý
      // 3. Đang ở trang logout
      if (currentPath === '/login' || 
          currentPath === '/sso_callback' || 
          currentPath.includes('/auth/') ||
          requestUrl.includes('/auth/user') ||
          requestUrl.includes('/auth/logout')) {
        // Chỉ xóa token, không redirect
        localStorage.removeItem('access_token');
        return Promise.reject(error);
      }
      
      // Token hết hạn hoặc không hợp lệ - xóa token
      localStorage.removeItem('access_token');
      
      // Lưu URL hiện tại để redirect sau khi login
      if (currentPath !== '/login' && currentPath !== '/sso_callback') {
        const returnUrl = window.location.origin + currentPath + window.location.search;
        sessionStorage.setItem('redirectUrl', returnUrl);
      }

      // Redirect đến trang login frontend (không phải backend URL)
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default axiosInstance; 
